import { UserProfileContext } from 'context/UserProfileContext';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useContext, useState } from 'react';
import { MutationFunction, QueryFunction, QueryKey, useMutation, UseMutationOptions, useQuery, UseQueryOptions } from 'react-query';

interface IHttpClientArguments {
	path?: string;
	method?: string;
	contentType?: string;
	headers?: HeadersInit;
	ignoreDefaultHeaders?: boolean;
	baseUrl?: string;
}

const defaultConfig: RequestInit = {
	credentials: 'same-origin',
	cache: 'no-cache',
	mode: 'cors',
	redirect: 'follow'
};
export interface KolmeoRequestHeaders {
	'x-version'?: '1';
	'x-kol-userprofile-id'?: string;
	Authorization?: string;
}
export interface UseHttpClient {
	abort: () => void;
	json: <TResponse = Promise<unknown>, TRequest = unknown>(data?: TRequest) => Promise<TResponse>;
	blob: <TResponse = Promise<unknown>, TRequest = unknown>(data?: TRequest) => Promise<string>;
	queryFn: any | (() => <TResponse = Promise<unknown>, TRequest = unknown>(data?: TRequest) => Promise<TResponse>);
	headers: KolmeoRequestHeaders;
	isAuthenticated: boolean;
}

const requestError = (message: string = 'Failed to call API', { headers }: Pick<Response, 'headers'>) => {
	const correlationId = headers.get('x-correlation-id');
	if (correlationId) return Error(`${message}.\n ${correlationId}`);
	return Error(`${message}.`);
};

export const useHttpClient = ({
	path,
	method,
	contentType = 'application/json',
	headers,
	ignoreDefaultHeaders = false,
	baseUrl = process.env.REACT_APP_Base_GraphQL_Url!
}: IHttpClientArguments): UseHttpClient => {
	const { token, userProfileId } = useContext(UserProfileContext);
	//const baseUrl: string = process.env.REACT_APP_Base_GraphQL_Url!;

	if (!baseUrl) throw Error('Missing Base Uri');
	if (!path) throw Error('Missing Request Uri');
	if (!method) throw Error('Missing Request Method');

	const [controller, setController] = useState<AbortController>(new AbortController());
	const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

	useEffect(() => {
		setIsAuthenticated(!!token && !!userProfileId);
	}, [token, userProfileId]);

	const abort = () => {
		controller.abort();
		setController(new AbortController());
	};

	let requestHeaders: KolmeoRequestHeaders & HeadersInit = { ...headers };

	const defaultRequestHeaders: KolmeoRequestHeaders & HeadersInit = {
		Authorization: `Bearer ${token}`,
		'Content-Type': contentType,
		'x-version': '1',
		...headers
	};

	if (!ignoreDefaultHeaders) {
		requestHeaders = { ...defaultRequestHeaders };
	}

	if (!!userProfileId) requestHeaders['x-kol-userprofile-id'] = `${userProfileId}`;

	const send = async <TRequest = unknown>(data?: TRequest) => {
		const body: string | undefined | null = !data ? null : JSON.stringify(data);

		return await fetch(`${baseUrl}${path}`, {
			...defaultConfig,
			signal: controller.signal,
			headers: requestHeaders,
			method,
			body
		});
	};

	const tryDeserialize = async (r: Response) => {
		let responseData;
		try {
			responseData = await r.json();
		} catch {
			responseData = undefined;
		}
		return responseData;
	};

	const json = async <TRequest = any, TResponse = any>(data?: TRequest) => {
		const response = await send(data);
		if (!response.ok) {
			if (response.status == 401) throw requestError('Authentication error.', response);
			throw await tryDeserialize(response);
		}

		if (response.status == 204) return null as any;
		return (await tryDeserialize(response)) as TResponse;
	};

	const blob = async <T1 = unknown>(data?: T1) =>
		new Promise<string>(async (resolve, _) => {
			const response = await send(data);
			if (!response.ok) {
				if (response.status == 401) throw requestError('Authentication error.', response);
				throw requestError('Error getting file.', response);
			}
			resolve(URL.createObjectURL(await response.blob()));
		});

	const queryFn = async () => await json();

	return { blob, json, abort, queryFn, headers: requestHeaders, isAuthenticated };
};
